/**
 * @fileoverview Admin Layout
 *
 * Purpose: Root layout for all admin panel pages.
 * Provides the admin-specific sidebar and access control.
 *
 * Protection:
 * - Requires authentication (via parent _auth route)
 * - Requires systemRole === "super_admin" (redirects non-admins to /dashboard)
 *
 * Wraps: /admin, /admin/invitations, /admin/users, /admin/organisations
 *
 * Dependencies:
 * - shadcn Sidebar (SidebarProvider, SidebarInset)
 * - AdminSidebar component
 * - Convex: users.queries.getCurrentUser
 */

import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "@/ui/layout/sidebar";
import { AdminSidebar } from "./-ui.admin-sidebar";
import { useEffect } from "react";

export const Route = createFileRoute("/_app/_auth/admin/_layout")({
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData(
      convexQuery(api.users.queries.getCurrentUser, {}),
    );
    if (!user || user.systemRole !== "super_admin") {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: AdminLayout,
});

/**
 * Admin layout component.
 * Checks that the current user has super_admin role before rendering.
 * Non-admins are silently redirected to the dashboard.
 */
function AdminLayout() {
  const { data: user, isLoading } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const navigate = useNavigate();

  // Redirect non-admins to dashboard
  useEffect(() => {
    if (!isLoading && user && user.systemRole !== "super_admin") {
      navigate({ to: "/dashboard" });
    }
  }, [isLoading, user, navigate]);

  // Show nothing while loading or if user is not an admin
  if (isLoading || !user || user.systemRole !== "super_admin") {
    return null;
  }

  return (
    <SidebarProvider>
      <AdminSidebar />
      <SidebarInset>
        <div className="flex flex-1 flex-col">
          <header className="flex h-16 shrink-0 items-center border-b bg-background px-3">
            <SidebarTrigger className="-ml-1 h-7 w-7 [&>svg]:size-4" />
          </header>
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
